import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  computed,
  signal,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Day } from '@progress/kendo-date-math';
import {
  DatePickerComponent,
  FormatSettings,
} from '@progress/kendo-angular-dateinputs';
import { IntlService } from '@progress/kendo-angular-intl';

@Component({
  selector: 'ups-kendo-datepicker-shared',
  templateUrl: './kendo-datepicker-shared.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => KendoDatepickerSharedComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class KendoDatepickerSharedComponent implements ControlValueAccessor {
  // Written by forms code (writeValue) and by Kendo callbacks outside this
  // component's change detection, so the template reads it as a signal.
  private readonly valueState = signal<Date | string>(undefined);

  @Input()
  get value(): Date | string {
    return this.valueState();
  }
  set value(value: Date | string) {
    this.valueState.set(value);
  }

  /**
   * What the Kendo DatePicker gets: it takes only a Date or null (a string throws in dev mode). A string value,
   * e.g. this picker's own dateToString output fed back through [(value)], is parsed as MM/dd/yyyy or ISO (WI 34037).
   */
  readonly pickerValue = computed<Date | null>(() => {
    const value = this.valueState();
    if (value == null || value === '') return null;
    if (value instanceof Date) return value;
    const parsed =
      this.intl.parseDate(value, ['MM/dd/yyyy', 'yyyy-MM-dd']) ??
      new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  });

  @Input() disabled: boolean;
  @Input() disabledDates: ((date: Date) => boolean) | Date[] | Day[];
  @Input() format: string | FormatSettings;
  @Input() placeholder: string;
  @Input() min: Date;
  @Input() max: Date;
  @Input() readOnlyInput: boolean;
  @Input() displayFormat = 'MM/dd/yyyy';

  @Input() isFullWidth: boolean;
  @Input() dateToString: boolean;

  @Output() valueChange: EventEmitter<Date | string> = new EventEmitter();

  @ViewChild('kendoDatePicker') datePicker: DatePickerComponent;

  // A no-op until a forms directive registers one: a picker bound by hand ([value] + (valueChange), the only safe
  // binding under signal forms) gets no registerOnTouched, and the template calls this on every blur.
  onTouched: () => void = () => undefined;
  private onChange: (value: Date | string) => void;

  constructor(private intl: IntlService) {}

  registerOnChange(fn: (value: Date) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  writeValue(value: Date): void {
    this.valueState.set(value);
  }

  onDateChange(value: Date): void {
    let outputValue: string | Date = null;
    // Mirrors the old [(value)] two-way binding, which wrote the raw Kendo
    // value (including null on clear) before this handler ran.
    this.valueState.set(value);
    if (value) {
      if (value.getFullYear() < 100) {
        value = new Date(
          value.getFullYear() + 2000,
          value.getMonth(),
          value.getDate()
        );
      }

      this.valueState.set(value);
      outputValue = this.dateToString
        ? this.intl.formatDate(value, 'MM/dd/yyyy')
        : value;
    }

    this.valueChange.emit(outputValue);
    if (this.onChange) {
      this.onChange(outputValue);
    }
  }
}
